/** @format */
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import { getProductListingResultsAvailableQuery } from '@toucan/common/services/search/product/queries';
import {
    FilterQuery,
    QueryResponse,
    SearchListingProduct,
    Query as SearchServiceQuery,
} from '@toucan/common/services/search/types';

import { MarketContext } from '@toucan/client/global/market';

import { getListingResults } from '@toucan/client/core/search';

import { Filter } from '@toucan/client/features/search/config/types';
import {
    getModelIdFromQuery,
    transformFiltersToSearchParams,
} from '@toucan/client/features/search/helpers';
import { usePriceModifierGroups } from '@toucan/client/features/search/hooks/use-model-price-modifier-groups';

import { useDebouncedValue } from './use-debounced-value';

function transformFilterQuery(filters: Filter[]) {
    const searchParams = transformFiltersToSearchParams(
        filters.filter((filter) => filter.selectedOptions.length > 0)
    );

    return searchParams.filterQuery as FilterQuery;
}

export const useResultsAvailable = ({
    baseQuery,
    filters,
}: {
    baseQuery: Partial<SearchServiceQuery>;
    filters: Filter[];
}) => {
    const debouncedFilters = useDebouncedValue(filters, 300);

    const debouncedFilterQuery = useMemo(
        () => transformFilterQuery(debouncedFilters),
        [debouncedFilters]
    );
    const { activeMarket } = useContext(MarketContext);

    const modelId = getModelIdFromQuery(baseQuery.filterQuery!);

    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );

    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Legacy code: do not use as an example.
    return useQuery({
        queryKey: ['search-results-available', debouncedFilterQuery],
        keepPreviousData: true,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: () => {
            return getListingResults<SearchListingProduct>(
                getProductListingResultsAvailableQuery(
                    baseQuery,
                    debouncedFilterQuery,
                    activeMarket.code,
                    modifierGroups
                ),
                activeMarket.iso,
                activeMarket.code
            );
        },
        select: (data: QueryResponse<SearchListingProduct>) => {
            return data.totalResults;
        },
    });
};
